import { datadogRum } from "@datadog/browser-rum";
import LogRocket from "logrocket";

import {
  logAnalyticsError,
  logPageNotFound,
} from "@brexhq/next/src/telemetry/logging";
import noop from "@brexhq/next/src/utils/noop";

import { EventNames } from "./analyticsEvents";
import { getAnonymousId } from "./initAnalytics";
import {
  AnalyticsApi,
  InitializedSegmentClient,
  LogAnalyticsError,
} from "./types";

export const createSegmentAnalytics = (
  client: InitializedSegmentClient,
  logError: LogAnalyticsError,
  logPageNotFound: (href: string) => void,
): AnalyticsApi => {
  let didEstablishUserIdentity = false;

  return {
    sendTiming(event) {
      client.track(event.type, { time: event.time });
    },
    trackEvent(event) {
      if (!event) return;
      if (event.value === EventNames.NONE) return;
      if (event.value === EventNames.PAGE_VIEWED) {
        const { properties } = event;
        const { url, title, search, referrer, path } = properties;
        return client.page(path, referrer, search, title, url);
      }

      if (event.value === EventNames.PAGE_NOT_FOUND) {
        logPageNotFound(event.properties.href);
      }

      /**
       * We want to find out if we are ever getting signup events before
       * the user is identified to guard against null ids in analytics events
       */
      if (event.value === EventNames.EARLY_ACCOUNT_SIGNUP) {
        /**
         * adding timeout here helps give enough time for the event to flush
         * trying this to make sure this event doesn't get lost
         * during the signup redirect.
         */
        window?.analytics?.timeout(500);

        /**
         * guardrail to ensure that we identify() the users linking them with a
         * user id before we fire this signup event.
         */
        if (!didEstablishUserIdentity) {
          logError(
            new Error("Soft signup occurred before user was identified"),
          );
        }
      }

      const { value, properties } = event;

      client.track(value, properties);
      window?.dataLayer?.push({
        event: value,
        ...properties,
      });
    },
    identify({ id, properties, integrations }) {
      client.identify(
        properties,
        id,
        /**
         * Add in custom integrations if exists
         */
        integrations,
      );
      if (!didEstablishUserIdentity && id && id !== "") {
        didEstablishUserIdentity = true;
      }
    },
  };
};

export const createDatadogAnalytics = (): AnalyticsApi => {
  return {
    sendTiming: noop,
    trackEvent(event) {
      if (!event) return;
      if (event.value === EventNames.NONE) return;
      try {
        datadogRum.addAction(event.value, {
          ...event.properties,
        });
      } catch (err) {
        console.error("Failed to track event in DataDog");
      }
    },
    identify({ id, properties }) {
      datadogRum.setUser({
        id: id || getAnonymousId(),
        ...properties,
      });
    },
  };
};

export const createLogRocketAnalytics = (
  logRocketInstance: typeof LogRocket,
): AnalyticsApi => {
  return {
    sendTiming: () => void 0,
    trackEvent(event) {
      // Sanitize properties before sending to LogRocket
      const sanitized = Object.entries(event.properties || {}).reduce(
        (acc, [key, value]) => {
          // Handle primitive values directly
          if (
            value === null ||
            value === undefined ||
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            acc[key] = value;
          }
          // Handle arrays of primitives directly
          else if (
            Array.isArray(value) &&
            value.every(
              (item) =>
                item === null ||
                typeof item === "string" ||
                typeof item === "number" ||
                typeof item === "boolean",
            )
          ) {
            acc[key] = value;
          }
          // Stringify everything else
          else {
            try {
              acc[key] = JSON.stringify(value);
            } catch (e) {
              acc[key] = `[unserializable: ${typeof value}]`;
            }
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      logRocketInstance.track(event.value, sanitized);
    },
    identify({ id, properties }) {
      // remove pii.
      const redactedProperties = { ...properties };
      const redactedKeys = ["firstName", "lastName", "email", "companyName"];
      redactedKeys.forEach((key) => delete redactedProperties[key]);
      if (id) {
        logRocketInstance.identify(id, redactedProperties);
      } else logRocketInstance.identify(redactedProperties);
    },
  };
};

export const createSegmentAnalyticsWithDecorations = (
  segmentClient: InitializedSegmentClient,
) => {
  const segmentAnalyticsApi = createSegmentAnalytics(
    segmentClient,
    logAnalyticsError,
    logPageNotFound,
  );

  segmentAnalyticsApi.trackEvent = mapToGoogleAnalyticsEventDecorator(
    segmentAnalyticsApi.trackEvent,
  );
  return segmentAnalyticsApi;
};

export const createGoogleAnalytics = (
  // @ts-expect-error where does UniversalAnalytics come from?
  googleClientAsync: Promise<UniversalAnalytics.ga>,
): AnalyticsApi => {
  return {
    sendTiming(event) {
      googleClientAsync.then((client) =>
        client("send", {
          hitType: "timing",
          timingCategory: "Lifecycle",
          timingVar: event.type,
          timingValue: event.time,
        }),
      );
    },
    trackEvent: noop,
    identify: noop,
  };
};

export const createCompositeAnalytics = ({
  isDisabled,
  children,
}: {
  isDisabled: () => boolean;
  children: AnalyticsApi[];
}): AnalyticsApi => {
  return {
    sendTiming(event) {
      if (isDisabled()) return;
      children.forEach((a) => a.sendTiming(event));
    },

    trackEvent(event) {
      if (isDisabled()) return;
      children.forEach((a) => a.trackEvent(event));
    },
    identify(event) {
      if (isDisabled()) return;
      children.forEach((a) => a.identify(event));
    },
  };
};

/**
 * Allows Google analytics to receive correct values from segment
 * @param trackEvent
 */
export const mapToGoogleAnalyticsEventDecorator =
  (trackEvent: AnalyticsApi["trackEvent"]): AnalyticsApi["trackEvent"] =>
  (event) => {
    if (event.value === EventNames.INTERNAL_LINK_CLICKED) {
      const withGoogleAnalyticsProperties = {
        ...event,
        properties: {
          ...event.properties,
          label: event.properties.target,
        },
      };

      return trackEvent(withGoogleAnalyticsProperties);
    }
    return trackEvent(event);
  };

export const createNullAnalyticsApi = () =>
  createCompositeAnalytics({ isDisabled: () => false, children: [] });

export const createMutinyAnalyticsApi = (): AnalyticsApi => {
  return {
    ...createNullAnalyticsApi(),
    trackEvent(event) {
      let finalEvent = "";

      switch (event.value) {
        case EventNames.FORM_STEP_VIEWED:
          finalEvent = [
            event.value,
            event.properties.form,
            event.properties.step,
          ].join(".");
          break;

        case EventNames.SSU_STEP_VIEWED:
          finalEvent = [event.value, event.properties.step].join(".");
          break;

        case EventNames.SIGNUP_USER_INFO_SUBMITTED:
          finalEvent = event.value;
          break;
      }

      // @ts-expect-error temp work around until mutiny support comes through
      window?.mutiny?.client?.trackConversion({ name: finalEvent });
    },
  };
};
